import {
  button_image_url, external_contact_form,
  extra_margin_bottom,
  extra_margin_right, hide_contact_form, logo_base64,
  primary_colour,
  preview_mode,
  secondary_colour, success_subtitle, success_title, widget_size
} from "../config";

export type ChannelTypes = 'whatsapp' | 'sms' | 'messenger' | 'instagram' | 'email' | 'unknown';
export class WidgetDomain {
  constructor(
    public widgetId: string,
    public merchantPublicId: string,
    public channels: ChannelDomain[],
    public logo: string,
    public locationName: string,
    public externalContactFormUrl?: string,
    public customStyles?: WidgetCustomStyles,
    public size?: "sm" | "md" | "lg",
    public hidePoweredBy?: boolean,
    public preventAutoOpen?: boolean,
    public delayAutoOpen?: number,
    public buttonTitle?: string,
    public merchantCountry?: string,
    public successTitle?: string,
    public hideContactForm?: boolean,
    public successSubtitle?: string,
    public buttonImageUrl?: string,
  ) {}
}
export class ChannelDomain {
  constructor(
    public id: string,
    public type: ChannelTypes,
    public handle: string,
    public link: string
  ) {}
}
export class WidgetCustomStyles {
  constructor(public primaryColour?: string, public secondaryColour?: string, public extraMarginBottom?: number, public extraMarginRight?: number) {}

  public static fromDto(
    customConfig?: CustomConfigDto
  ): WidgetCustomStyles | undefined {
    if (customConfig) {
      return new WidgetCustomStyles(
        customConfig.primary_colour,
        customConfig.secondary_colour
      );
    }
    return undefined;
  }
}

type CustomConfigDto = {
  primary_colour?: string;
  secondary_colour?: string;
  contact_form_url?: string;
  hide_powered_by?: boolean;
  prevent_auto_open?: boolean;
  delay_auto_open?: number;
  success_title?: string;
  hide_contact_form?: boolean;
  success_subtitle?: string;
  size?: "sm" | "md" | "lg";
  button_image_url?: string;
};

type WidgetDto = {
  id: string;
  merchant_public_id: string;
  channels: {
    id: string;
    channel: string;
    handle: string;
    link: string;
  }[];
  custom_config?: CustomConfigDto;
  location_name: string;
  button_title?: string;
  logo: string;
  merchant_country?: string;
};

/**
 * Delete config cached by earlier widget versions.
 *
 * Those versions stored widget config in localStorage under the widget id. The one-hour
 * expiry only stopped stale entries being *read* — nothing ever deleted them — so every
 * returning visitor still carries whatever was written on their last visit, indefinitely.
 *
 * Removing the write without this would strand those values on exactly the devices most
 * likely to be inspected, and make the "writes nothing to localStorage" line in the cookie
 * table we hand Controllers false. So the removal is explicit rather than implied.
 *
 * localStorage belongs to the HOST PAGE, not to us — the widget is embedded in a
 * dealership's own site, and a dealership could plausibly key something of its own by the
 * widget id we gave them. So this verifies the value is ours before deleting it, rather than
 * clearing whatever happens to sit under that key. Deleting someone else's data to tidy up
 * our own is not a trade we get to make.
 *
 * Retire this once returning visitors have all had one page load on a version containing it.
 */
function retireLegacyConfigCache(widgetId: string) {
  try {
    const stored = localStorage.getItem(widgetId);
    if (stored === null || !isLegacyConfigCache(stored)) {
      return;
    }
    localStorage.removeItem(widgetId);
  } catch {
    // Storage access throws when it is disabled or partitioned (Safari private browsing,
    // some embedded contexts). There is nothing to clean up in that case, and this must
    // never prevent the widget from loading — getAllByIds catches to an empty widget list.
  }
}

/**
 * Does this value carry the shape LocalStorageService.setWithExpiration wrote?
 *
 * That was `{ content: <encoded string>, expiration: <epoch ms> }`. Both fields together are
 * a distinctive enough signature: a host-page value would have to be JSON, an object, and
 * carry exactly those two typed fields, under a key that is exactly one of our widget UUIDs.
 * Expiry is deliberately not checked — long-expired entries are the ones we most want gone.
 */
function isLegacyConfigCache(stored: string): boolean {
  let parsed: unknown;
  try {
    parsed = JSON.parse(stored);
  } catch {
    return false;
  }
  if (typeof parsed !== "object" || parsed === null) {
    return false;
  }
  const candidate = parsed as { content?: unknown; expiration?: unknown };
  return typeof candidate.content === "string" && typeof candidate.expiration === "number";
}

export default class WidgetService {
  public static async getAllByIds(
    merchant_uuids: string[]
  ): Promise<WidgetDomain[]> {
    const widgets: WidgetDomain[] = [];

    try {
      // Widget config is fetched every load rather than cached in localStorage. The cache
      // it replaced was a performance optimisation only — on a miss this same request ran —
      // but it wrote to the visitor's device on page load, before any interaction with the
      // chat. That is outside the ePrivacy/PECR "strictly necessary for a service the user
      // explicitly requested" exemption, so it would have needed a consent gate wired up to
      // each dealership's own cookie tool. Dropping it is cheaper than gating it, and means
      // the widget writes nothing to localStorage at all.
      const query = new URLSearchParams();
      merchant_uuids
        .filter(mid => !!mid)
        .map(mid => mid.replace(/[{}]/g, ""))
        .forEach((merchant_uuid) => {
          retireLegacyConfigCache(merchant_uuid);
          query.append("widget_ids", merchant_uuid);
        });

      if (!query.toString()) {
        return widgets;
      }

      const response = await fetch(
        `${process.env.REACT_APP_FUZEY_API_URL}/public/widget?${query.toString()}`,
        preview_mode ? { cache: "no-store" } : undefined
      );
      const response_data = (await response.json()) as WidgetDto[];

      response_data.forEach((widgetDto) => {
        const channels = widgetDto.channels
          .map(
            (channel) =>
              new ChannelDomain(
                channel.id,
                this.convertFromChannel(channel.channel),
                channel.handle,
                channel.link
              )
          )
          .filter((channel) => channel.type !== "unknown");

        const widget = new WidgetDomain(
          widgetDto.id,
          widgetDto.merchant_public_id,
          channels,
          widgetDto.logo,
          widgetDto.location_name,
          widgetDto.custom_config?.contact_form_url,
          WidgetCustomStyles.fromDto(widgetDto.custom_config),
          widgetDto.custom_config?.size,
          widgetDto.custom_config?.hide_powered_by,
          widgetDto.custom_config?.prevent_auto_open,
          widgetDto.custom_config?.delay_auto_open,
          widgetDto.button_title,
          widgetDto.merchant_country,
          widgetDto.custom_config?.success_title,
          widgetDto.custom_config?.hide_contact_form,
          widgetDto.custom_config?.success_subtitle,
          widgetDto.custom_config?.button_image_url,
        );

        widgets.push(this.enrichWithPreviewConfig(widget));

        return;
      });

      return widgets;
    } catch {
      return [];
    }
  }

  private static enrichWithPreviewConfig(widget: WidgetDomain): WidgetDomain {
    return {
      ...widget,
      customStyles: {
        primaryColour: primary_colour || widget.customStyles?.primaryColour,
        secondaryColour: secondary_colour || widget.customStyles?.secondaryColour,
        extraMarginBottom: extra_margin_bottom || 0,
        extraMarginRight: extra_margin_right || 0,
      },
      size: widget_size ?? widget.size ?? "md",
      externalContactFormUrl:
        external_contact_form || widget.externalContactFormUrl,
      logo: logo_base64 || widget.logo,
      successTitle: success_title || widget.successTitle,
      hideContactForm: hide_contact_form === "true" || widget.hideContactForm,
      successSubtitle: success_subtitle || widget.successSubtitle,
      buttonImageUrl: button_image_url || widget.buttonImageUrl,
    };
  }

  private static convertFromChannel(channelType: string): ChannelTypes {
    switch (channelType) {
      case "facebook":
        return "messenger";
      case "instagram":
        return "instagram";
      case "sms":
        return "sms";
      case "whatsapp":
        return "whatsapp";
      case "email":
        return "email";
      default:
        return "unknown";
    }
  }
}
